import { dom } from "@fortawesome/fontawesome-svg-core";
import debounce from "lodash/debounce";
import { errorText } from "../../utils/error-messages";

interface Place {
  title: string;
  lat: number;
  lng: number;
}

interface PlacesResponse {
  data: Place[];
  metadata?: { error_message?: string };
}

const PLACES_URL = "/website-api/area/places/?q=";
const RELEVANT_USERS_URL = "/website-api/landing/relevant-users/?";
// Keep the "loading" hint on screen for at least this long so the user notices something happened.
const MIN_LOADING_DISPLAY_MS = 1000;
export const initLandingAutocomplete = () => {
  // The page-type value doubles as the marker distinguishing this widget from the search autocomplete.
  const roots = document.querySelectorAll<HTMLDivElement>(".autocomplete[data-page-type]");

  roots.forEach((root) => {
    setupLandingAutocomplete(root);
  });
};

const setupLandingAutocomplete = (root: HTMLDivElement) => {
  const input = root.querySelector<HTMLInputElement>('[data-autocomplete-el="input"]');
  const loader = root.querySelector<HTMLDivElement>('[data-autocomplete-el="loader"]');
  const resultsContainer = root.querySelector<HTMLDivElement>('[data-autocomplete-el="results"]');
  const addressResults = root.querySelector<HTMLDivElement>(
    '[data-autocomplete-el="address-results"]',
  );
  const searchButton = root.querySelector<HTMLButtonElement>('[data-autocomplete-el="search"]');
  const form = root.querySelector<HTMLFormElement>("form");

  if (!input || !loader || !resultsContainer || !addressResults) {
    return;
  }

  let prevController: AbortController | undefined;

  const show = (element: HTMLElement) => element.classList.remove("d-none");
  const hide = (element: HTMLElement) => element.classList.add("d-none");

  const pageType = () => root.dataset.pageType ?? "";

  // Throws on error statuses (hiding the loader first); for 403 it surfaces the API error message.
  // Returns the pending (always-rejecting) 403 promise so callers can let it reject, or undefined to continue.
  const guardResponse = (response: Response): Promise<never> | undefined => {
    if (response.status === 401) {
      window.location.href = "/";
    }
    if (response.status === 429) {
      hide(loader);
      alert(errorText("rateLimited"));

      throw new Error("Too many requests");
    }
    if (response.status === 403) {
      hide(loader);

      return response
        .json()
        .then((jsonResponse: PlacesResponse) => {
          alert(jsonResponse.metadata?.error_message || errorText("proxy"));
          throw new Error("VPN Detected (403)");
        })
        .catch((_parseError) => {
          // If JSON parsing fails, use default message
          alert(errorText("proxy"));
          throw new Error("VPN Detected (403)");
        });
    }
    if (response.status >= 500) {
      hide(loader);
      alert(errorText("generic"));

      throw new Error("500 problem.");
    }

    return undefined;
  };

  const fetchSearchResults = (url: string, signal: AbortSignal): Promise<Place[]> => {
    return fetch(url, { signal })
      .then((response) => guardResponse(response) ?? response.json())
      .then((jsonResponse: PlacesResponse) => (!jsonResponse.data.length ? [] : jsonResponse.data))
      .catch((_e) => []);
  };

  // The translated "reusable.loading" text is already rendered (visually hidden) inside the loader.
  const loadingText = loader.querySelector<HTMLElement>(".visually-hidden")?.textContent?.trim();

  const fetchRelevantProfiles = (url: string, signal: AbortSignal) => {
    const relevantUsers = document.querySelector<HTMLElement>(".relevant-users");
    const startedAt = Date.now();

    // Show the loading hint straight away so the user knows something is happening.
    // Overlay it on top of the current results (rather than replacing them) so the container keeps
    // its natural height and nothing on the page shifts while loading.
    if (relevantUsers && loadingText) {
      relevantUsers.classList.add("position-relative");
      const overlay = document.createElement("div");
      overlay.className =
        "loading-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center fw-bold bg-white bg-opacity-75";
      overlay.textContent = loadingText;
      relevantUsers.appendChild(overlay);
    }

    // Remove the loading overlay and its positioning helper (used on both success and error paths).
    const clearLoadingOverlay = () => {
      if (relevantUsers) {
        relevantUsers.classList.remove("position-relative");
        relevantUsers.querySelector(".loading-overlay")?.remove();
      }
    };

    fetch(url, { signal })
      .then((response) => guardResponse(response) ?? response.text())
      .then((htmlString: string) => {
        const renderResults = () => {
          hide(loader);

          clearLoadingOverlay();

          if (relevantUsers) {
            relevantUsers.innerHTML = htmlString;
          }

          // Tell FontAwesome to search for new icons.
          // Fix the bug with reviews icons not being rendered.
          dom.i2svg();
        };

        // Keep the loading hint visible for at least MIN_LOADING_DISPLAY_MS before swapping in results.
        const remaining = MIN_LOADING_DISPLAY_MS - (Date.now() - startedAt);

        if (remaining > 0) {
          window.setTimeout(renderResults, remaining);
        } else {
          renderResults();
        }
      })
      .catch((_e) => {
        // On any failure (429/403/500 from guardResponse, or a network/abort error) make sure the
        // loading hint doesn't linger; guardResponse already hides the loader for HTTP errors.
        hide(loader);
        clearLoadingOverlay();
      });
  };

  const loadRelevantProfiles = (e: Event) => {
    e.preventDefault();

    hide(resultsContainer);

    const link = e.currentTarget as HTMLAnchorElement;
    const url = `${RELEVANT_USERS_URL}lat=${link.dataset.lat}&lng=${link.dataset.lng}&page_type=${pageType()}`;
    const controller = new AbortController();
    prevController = controller;
    const signal = controller.signal;

    show(loader);

    const debounceFetch = debounce(fetchRelevantProfiles, 400);

    debounceFetch(url, signal);
  };

  const showResultsWithData = (places: Place[]) => {
    show(resultsContainer);

    let linkCollections = "";

    for (const place of places) {
      // No base url is configured for the landing widget, so links stay relative (query only).
      const url = `?query=${place.title}&lat=${place.lat}&lng=${place.lng}&location_search=address`;

      /* Add tabindex to a element to make relatedTarget work in Safari */
      linkCollections += `<a href="${url}" data-type="href-action" tabindex="0" data-lat="${place.lat}" data-lng="${place.lng}">${place.title}</a>`;
    }

    addressResults.innerHTML = linkCollections;

    addressResults.querySelectorAll<HTMLAnchorElement>("a").forEach((link) => {
      link.addEventListener("click", loadRelevantProfiles);
    });
  };

  const performSearchAndShowRelevantProfiles = (e: Event) => {
    e.preventDefault();

    const query = input.value.trim();
    const controller = new AbortController();
    const signal = controller.signal;

    if (!query) {
      input.focus();
      return;
    }

    if (prevController) {
      prevController.abort();
    }

    hide(resultsContainer);
    show(loader);

    const url = `${PLACES_URL}${query}`;

    prevController = controller;

    const debouncedFetch = debounce((placesUrl: string, placesSignal: AbortSignal) => {
      return fetchSearchResults(placesUrl, placesSignal).then((places) => {
        if (!places.length) {
          hide(loader);
          return;
        }

        const relevantUrl = `${RELEVANT_USERS_URL}lat=${places[0].lat}&lng=${places[0].lng}&page_type=${pageType()}`;
        const relevantController = new AbortController();
        prevController = relevantController;
        const relevantSignal = relevantController.signal;

        const debounceFetch = debounce(fetchRelevantProfiles, 400);

        debounceFetch(relevantUrl, relevantSignal);
      });
    }, 400);

    debouncedFetch(url, signal);
  };

  // Created once (not per keystroke) so rapid typing collapses into a single fetch once the user
  // pauses, instead of firing a separate delayed fetch for every character.
  const debouncedPlacesSearch = debounce((placesUrl: string, placesSignal: AbortSignal) => {
    return fetchSearchResults(placesUrl, placesSignal).then((places) => {
      // If this request was superseded by a newer keystroke it will have been aborted; skip it so we
      // don't hide the spinner or flash empty results before the newer request resolves.
      if (placesSignal.aborted) {
        return;
      }

      hide(loader);
      showResultsWithData(places);
    });
  }, 400);

  const performSearch = (e: Event) => {
    e.preventDefault();

    const query = input.value.trim();

    if (!query) {
      input.focus();
      return;
    }

    if (prevController) {
      prevController.abort();
    }

    const controller = new AbortController();
    prevController = controller;

    show(loader);

    debouncedPlacesSearch(`${PLACES_URL}${query}`, controller.signal);
  };

  const showResults = () => {
    if (!input.value.trim()) {
      return;
    }

    show(resultsContainer);
  };

  const hideResults = (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    if (relatedTarget && relatedTarget.dataset.type === "href-action") {
      return;
    }

    hide(resultsContainer);
  };

  input.addEventListener("focus", showResults);
  input.addEventListener("blur", hideResults);
  input.addEventListener("input", performSearch);

  searchButton?.addEventListener("blur", hideResults);
  searchButton?.addEventListener("click", performSearchAndShowRelevantProfiles);
  form?.addEventListener("submit", performSearchAndShowRelevantProfiles);
};
